import styled from 'styled-components'
import { hideVisually } from 'polished'
import { getProductDetails } from '@/features/shared/utils/getProductDetails'
import { Body, Headline } from '@shipt/design-system-typography'
import { Box } from '@shipt/design-system-layouts'
import { formatPrice } from '@/features/shared/utils/formatter'
import { useLoyaltyPrice } from '@/features/shop/hooks/useLoyaltyPrice'

type Props = {
  product: {
    bogo: boolean
    couponId: string
    totalPrice: number
    totalSalePrice: number
    original_price: number
    totalOriginalPrice: number
    cpgPromotion?: { category_id: number | undefined } | null
    descriptionLabels: string[]
    type: string
    hasCustomLabel: boolean
    onSale: boolean
    price: number
    pricing?: { loyalty: { price: number } }
    salePrice: number
    size: string
    quantity: number
  }
}

export const ProductDetailPrice = ({ product }: Props) => {
  const { onSale, price, salePrice, size, totalOriginalPrice } = product

  const { shouldShowLoyaltyPrice, loyaltyPrice, formattedLoyaltyPrice } = useLoyaltyPrice(product)
  const isLoyalty = !onSale && shouldShowLoyaltyPrice
  const hasDiscountedPrice = onSale || isLoyalty

  const {
    descriptionLabel,
    formattedPrice,
    formattedSalePrice,
    isByWeightProduct,
    isCustomByWeightProduct,
  } = getProductDetails(product)

  const renderDiscountedPrice = () => {
    const discountText = isLoyalty ? 'Loyalty' : 'Sale'
    return (
      <>
        <SalePrice size="lg">
          <HiddenPriceText>{discountText} unit price is </HiddenPriceText>
          {isLoyalty ? formattedLoyaltyPrice : formattedSalePrice}
        </SalePrice>
        <Box marginRight="xs">
          <OriginalPrice>
            <HiddenPriceText>Original unit price is </HiddenPriceText>
            {isLoyalty ? formatPrice(totalOriginalPrice || price) : formattedPrice}
          </OriginalPrice>
        </Box>
      </>
    )
  }

  const renderPrice = () => {
    return (
      <Price size="lg" aria-label={`Unit price: ${formattedPrice}`}>
        {formattedPrice}
      </Price>
    )
  }

  const renderLabel = () => {
    return (
      <Box margin={['xs', 'xs', 0, 0]}>
        <Body as="span" aria-hidden="true" variant="secondary">
          {`per ${descriptionLabel}`}
        </Body>
      </Box>
    )
  }

  const renderSubText = () => {
    let subtext = size
    if (isByWeightProduct) subtext = `per ${descriptionLabel}`
    if (isCustomByWeightProduct) {
      let unit

      if (onSale) {
        unit = salePrice
      } else if (isLoyalty) {
        unit = loyaltyPrice
      } else {
        unit = price
      }

      subtext = `$${unit} ${size}`
    }
    return (
      <Body aria-label={`Unit price is based on a size of ${subtext}.`} variant="secondary">
        {subtext}
      </Body>
    )
  }

  return (
    <Wrapper>
      {hasDiscountedPrice ? renderDiscountedPrice() : renderPrice()}
      {!!isCustomByWeightProduct && renderLabel()}
      {renderSubText()}
    </Wrapper>
  )
}

const HiddenPriceText = styled.span`
  ${hideVisually()};
`
const Wrapper = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
`

const SalePrice = styled(Headline)`
  color: ${({ theme }) => theme.color.background.feedback.promotion.loud};
  margin-right: 0.25rem;
`

const OriginalPrice = styled(Body)`
  text-decoration: line-through;
`

const Price = styled(Headline)`
  color: ${({ theme }) => theme.black};
  margin-right: 0.5rem;
`
